/**
 * Calendar-aware date-range picker (SCH.1).
 *
 * A month grid that plots the *existing* project schedule (from the shared
 * calendar endpoint) so a date is chosen with the surrounding crew load in
 * view, never blind. Travel-site range selection: click a start day, then an
 * end day (with a hover preview of the span); the end is optional — a single
 * click leaves it a one-day target. A legend keys the status colors, and busy
 * days are tinted + dotted so they stand out at a glance.
 *
 * Day strings compare lexicographically (YYYY-MM-DD === chronological), so
 * range math is plain string comparison — no parsing.
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Flag, X } from 'lucide-react';
import type { ProjectStatus } from '@vari-crew/schemas';
import { PROJECT_STATUS_LABELS } from '@vari-crew/schemas';
import { useProjectCalendar } from '@/api/calendar';
import { cn } from '@/lib/cn';
import {
  addMonths,
  bucketByDay,
  monthGrid,
  monthLabel,
  parseDay,
  toDayStr,
} from '@/lib/calendarGrid';

const STATUS_DOT: Record<ProjectStatus, string> = {
  requested: 'bg-status-requested',
  scheduled: 'bg-status-scheduled',
  en_route: 'bg-status-enroute',
  on_site: 'bg-status-onsite',
  punch: 'bg-status-punch',
  pending_closeout: 'bg-status-pendingcloseout',
  complete: 'bg-status-complete',
  blocked: 'bg-status-blocked',
  cancelled: 'bg-status-cancelled',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLabel(day: string): string {
  return parseDay(day).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function shortDay(day: string): string {
  return parseDay(day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function isWeekend(day: string): boolean {
  const dow = parseDay(day).getDay(); // 0=Sun … 6=Sat
  return dow === 0 || dow === 6;
}

function spanDays(start: string, end: string): number {
  const ms = parseDay(end).getTime() - parseDay(start).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

export interface CalendarDateRange {
  start: string | null;
  end: string | null;
}

export interface CalendarDatePickerProps {
  /** Selected start day 'YYYY-MM-DD', or null. */
  start: string | null;
  /** Selected end day (optional). */
  end: string | null;
  onChange: (range: CalendarDateRange) => void;
  className?: string;
  /** This project's requested/committed days ('YYYY-MM-DD') — softly outlined so
   *  the supervisor sees the window the PM asked for while picking. */
  requestedDays?: string[];
  /** Word for the highlighted window in the context banner. */
  requestedLabel?: string;
  /** Hard deadline day ('YYYY-MM-DD') — flagged as the must-finish-by date. */
  deadline?: string | null;
  /** Requested working-hours label (e.g. "8:00 AM – 5:00 PM") shown as context. */
  hoursLabel?: string | null;
  /** When `onIncludeWeekendsChange` is provided, shows a clearly-visible weekend
   *  include/exclude toggle and de-emphasizes (disables) weekend cells while
   *  excluded — so a multi-week range skips Sat/Sun. Controlled: the parent owns
   *  the value and shares it with the schedule generator (DailyHoursPicker). */
  includeWeekends?: boolean;
  onIncludeWeekendsChange?: (include: boolean) => void;
}

export function CalendarDatePicker({
  start,
  end,
  onChange,
  className,
  requestedDays,
  requestedLabel = 'Requested',
  deadline,
  hoursLabel,
  includeWeekends,
  onIncludeWeekendsChange,
}: CalendarDatePickerProps) {
  const today = toDayStr(new Date());
  const weekendToggleOn = !!onIncludeWeekendsChange;
  const excludeWeekends = weekendToggleOn && !includeWeekends;
  const requestedSet = useMemo(() => new Set(requestedDays ?? []), [requestedDays]);
  const requestedSpan = useMemo(() => {
    if (!requestedDays || requestedDays.length === 0) return null;
    const sorted = [...requestedDays].sort();
    return { first: sorted[0]!, last: sorted[sorted.length - 1]! };
  }, [requestedDays]);
  const hasContext = requestedSet.size > 0 || !!deadline || !!hoursLabel;
  const [view, setView] = useState(() => {
    const base = start ? parseDay(start) : new Date();
    return { year: base.getFullYear(), month0: base.getMonth() };
  });
  const [hoverDay, setHoverDay] = useState<string | null>(null);

  const cells = useMemo(
    () => monthGrid(view.year, view.month0, today),
    [view, today],
  );
  const { data, isLoading } = useProjectCalendar(cells[0]!.day, cells[41]!.day);
  const items = data?.items ?? [];
  const byDay = useMemo(() => bucketByDay(items), [items]);

  // Distinct statuses present this month → a dynamic legend (only what's shown).
  const legendStatuses = useMemo(() => {
    const seen = new Set<ProjectStatus>();
    for (const it of items) seen.add(it.status);
    return [...seen];
  }, [items]);

  // Pick start, then end (≥ start); a click before start restarts; a click once
  // both are set begins a new selection. End stays null for a single-day target.
  function handleClick(day: string) {
    if (!start || (start && end)) {
      onChange({ start: day, end: null });
    } else if (day < start) {
      onChange({ start: day, end: null });
    } else if (day === start) {
      onChange({ start: day, end: null });
    } else {
      onChange({ start, end: day });
    }
  }

  const pickingEnd = !!start && !end;
  const previewEnd = pickingEnd && hoverDay && hoverDay > start ? hoverDay : null;

  // The schedule inside the chosen window, grouped by date — what the user is
  // weighing this request against. A multi-day project lists under each of its
  // days. (Days are within the fetched grid range, so items cover them.)
  const windowEnd = end ?? start;
  const windowDays: { day: string; projects: typeof items }[] = [];
  if (start) {
    let d = parseDay(start);
    const last = parseDay(windowEnd!);
    let guard = 0;
    while (d <= last && guard < 120) {
      const dayStr = toDayStr(d);
      const onDay = items.filter(
        (it) => it.start_date <= dayStr && it.end_date >= dayStr,
      );
      if (onDay.length) windowDays.push({ day: dayStr, projects: onDay });
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      guard += 1;
    }
  }

  return (
    <div className={cn('rounded-lg border border-line bg-bg p-3', className)}>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => setView((v) => addMonths(v.year, v.month0, -1))}
          className="p-1 rounded hover:bg-fill-soft text-ink-dim"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-semibold">
          {monthLabel(view.year, view.month0)}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => setView((v) => addMonths(v.year, v.month0, 1))}
          className="p-1 rounded hover:bg-fill-soft text-ink-dim"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {weekendToggleOn && (
        <label className="mb-2 flex flex-wrap items-center gap-1.5 rounded-md border border-line bg-fill-soft px-2.5 py-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!includeWeekends}
            onChange={(e) => onIncludeWeekendsChange?.(e.target.checked)}
          />
          <span className="font-medium">Include weekends</span>
          <span className="text-ink-faint">— off, the crew works Mon–Fri (Sat/Sun skipped).</span>
        </label>
      )}

      {/* This project's constraints — the window the PM requested, the working
          hours, and the hard deadline — so the date is picked against them. */}
      {hasContext && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-fill-soft px-2 py-1.5 text-[11px]">
          {requestedSpan && (
            <span className="inline-flex items-center gap-1 text-ink-dim">
              <span className="h-2.5 w-2.5 rounded-sm border border-accent/60 bg-accent/15" />
              {requestedLabel} {shortDay(requestedSpan.first)}
              {requestedSpan.last !== requestedSpan.first ? ` – ${shortDay(requestedSpan.last)}` : ''}
            </span>
          )}
          {hoursLabel && (
            <span className="inline-flex items-center gap-1 text-ink-dim">
              <Clock className="h-3 w-3 text-ink-faint" /> {hoursLabel}
            </span>
          )}
          {deadline && (
            <span className="inline-flex items-center gap-1 font-medium text-status-blocked-fg">
              <Flag className="h-3 w-3" fill="currentColor" /> Deadline {shortDay(deadline)}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[10px] font-medium text-ink-faint">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" onMouseLeave={() => setHoverDay(null)}>
        {cells.map((c) => {
          const dayItems = byDay.get(c.day) ?? [];
          const statuses = [...new Set(dayItems.map((d) => d.status))];
          const isStart = c.day === start;
          const isEnd = c.day === end && end !== start;
          const inCommitted = !!(start && end && c.day > start && c.day < end);
          const inPreview = !!(previewEnd && c.day > start! && c.day <= previewEnd);
          const isEndpoint = isStart || isEnd;
          const busy = dayItems.length > 0;
          // This project's own context (vs. the other-project load above).
          const isRequested = requestedSet.has(c.day);
          const isDeadline = !!deadline && c.day === deadline;
          // Excluded weekends are dimmed + not selectable — but never disable a
          // day that's already a chosen endpoint (so an existing pick stays usable).
          const excluded = excludeWeekends && isWeekend(c.day) && !isStart && !isEnd;
          return (
            <button
              key={c.day}
              type="button"
              disabled={excluded}
              onClick={() => handleClick(c.day)}
              onMouseEnter={() => setHoverDay(c.day)}
              title={[
                isRequested ? 'Requested by the PM' : '',
                isDeadline ? 'Hard deadline' : '',
                busy ? `${dayItems.length} project${dayItems.length === 1 ? '' : 's'} scheduled` : '',
              ]
                .filter(Boolean)
                .map((s) => `${s} · `)
                .join('') + dayLabel(c.day)}
              className={cn(
                'relative h-12 rounded-md border text-xs flex flex-col items-center justify-start pt-1 transition-colors',
                c.inMonth ? 'text-ink' : 'text-ink-faint/50',
                isEndpoint
                  ? 'bg-accent text-white border-accent font-semibold'
                  : inCommitted || inPreview
                    ? 'bg-accent/15 border-accent/40'
                    : isRequested
                      ? 'bg-accent/5 border-accent/50 hover:bg-accent/10'
                      : busy
                        ? 'bg-fill-soft border-line hover:bg-fill'
                        : 'border-line hover:bg-fill-soft',
                // Hard deadline: a clear red outline on top of whatever state.
                isDeadline && 'ring-2 ring-inset ring-status-blocked-fg',
                // Weekend excluded from the schedule — dimmed + not selectable.
                excluded && 'opacity-40 cursor-not-allowed',
              )}
            >
              {isDeadline && (
                <Flag
                  className="absolute top-0.5 left-0.5 w-3 h-3 text-status-blocked-fg"
                  fill="currentColor"
                  aria-hidden
                />
              )}
              <span className={cn('leading-none', c.isToday && !isEndpoint && 'font-bold underline')}>
                {Number(c.day.slice(8))}
              </span>
              {busy && (
                <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full text-[9px] font-semibold bg-ink/10 text-ink-dim">
                  {dayItems.length}
                </span>
              )}
              {statuses.length > 0 && (
                <span className="mt-auto mb-1 flex items-center gap-0.5">
                  {statuses.slice(0, 4).map((st) => (
                    <span
                      key={st}
                      className={cn(
                        'w-2 h-2 rounded-full ring-1 ring-white/70',
                        STATUS_DOT[st],
                      )}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend / key */}
      {legendStatuses.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2">
          <span className="text-[10px] uppercase tracking-wide text-ink-faint">
            On the calendar
          </span>
          {legendStatuses.map((st) => (
            <span key={st} className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
              <span className={cn('w-2 h-2 rounded-full', STATUS_DOT[st])} />
              {PROJECT_STATUS_LABELS[st]}
            </span>
          ))}
        </div>
      )}

      {/* Selection summary + the schedule inside the chosen window, by date */}
      <div className="mt-2 border-t border-line pt-2 min-h-[2.5rem]">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 text-xs">
            {isLoading ? (
              <span className="text-ink-faint">Loading schedule…</span>
            ) : !start ? (
              <span className="text-ink-faint">
                Pick a start day{pickingEnd ? '' : ', then an end day (optional).'}
              </span>
            ) : (
              <span className="font-medium">
                {end && end !== start
                  ? `${shortDay(start)} – ${shortDay(end)} · ${spanDays(start, end)} days`
                  : pickingEnd
                    ? `${shortDay(start)} — now pick an end day, or leave as a single day`
                    : shortDay(start)}
              </span>
            )}
          </div>
          {start && (
            <button
              type="button"
              onClick={() => onChange({ start: null, end: null })}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {start &&
          !isLoading &&
          (windowDays.length === 0 ? (
            <div className="text-[11px] text-ink-faint mt-1">
              Nothing else scheduled in this window.
            </div>
          ) : (
            <div className="mt-1.5 space-y-1.5 max-h-44 overflow-auto pr-1">
              {windowDays.map(({ day, projects }) => (
                <div key={day}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                    {dayLabel(day)}
                  </div>
                  <ul className="mt-0.5 space-y-0.5">
                    {projects.map((p) => (
                      <li key={p.id} className="flex items-center gap-1.5 text-xs">
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full shrink-0',
                            STATUS_DOT[p.status],
                          )}
                        />
                        <span className="truncate">{p.title}</span>
                        {p.time_label ? (
                          <span className="text-ink-faint shrink-0">· {p.time_label}</span>
                        ) : (
                          p.location && (
                            <span className="text-ink-faint shrink-0 truncate">
                              · {p.location}
                            </span>
                          )
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}
